/**
 * ScreeningQuiz.jsx
 * Renders screening questions as an interactive quiz card.
 * The user must answer all questions before the candidate can be shortlisted.
 * On submit, sends answers to the backend for LLM-based evaluation.
 */

import React, { useState } from "react";
import "../ChatPage.css";

export default function ScreeningQuiz({ data, onSubmit, disabled }) {
  const questions = data?.questions || [];
  const [answers, setAnswers] = useState(
    () => questions.map(() => "")
  );
  const [status, setStatus] = useState("");
  const [comment, setComment] = useState("");
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (index, value) => {
    setAnswers((prev) => {
      const updated = [...prev];
      updated[index] = value;
      return updated;
    });
  };

  const isCommentValid = status === "REJECT" || comment.trim().length > 0;
  const allAnswered = answers.every((a) => a.trim().length > 0) && status !== "" && isCommentValid;

  const handleSubmit = () => {
    if (!allAnswered || submitted || disabled) return;
    setSubmitted(true);

    // Build the payload with questions + user answers
    const payload = {
      answers: questions.map((q, i) => ({
        ...q,
        question: q.screenQuestion || q.question || "",
        correctAnswer: q.screenAnswer || q.answer || q.correctAnswer || "",
        given_answer: answers[i],
      })),
      shortlist_action: data.shortlist_action,
      candidateId: data.candidateId,
      jobId: data.jobId,
      screeningRecordId: data.screeningRecordId,
      status: status,
      comment: comment,
    };

    onSubmit(payload);
  };

  if (questions.length === 0) {
    return (
      <div className="hn-screening-card">
        <p className="hn-screening-empty">No screening questions found.</p>
      </div>
    );
  }

  return (
    <div className="hn-screening-card">
      {/* Header */}
      <div className="hn-screening-header">
        <span className="hn-screening-badge">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M9 11l3 3L22 4" />
            <path d="M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11" />
          </svg>
          Screening Questions
        </span>
        <span className="hn-screening-count">
          {questions.length} question{questions.length !== 1 ? "s" : ""}
        </span>
      </div>

      {/* Questions */}
      <div className="hn-screening-questions">
        {questions.map((q, i) => {
          const questionText = q.screenQuestion || q.question || `Question ${i + 1}`;
          return (
            <div key={i} className="hn-screening-question">
              <label className="hn-screening-label">
                <span className="hn-screening-num">{i + 1}.</span>
                {questionText}
              </label>
              <input
                type="text"
                className="hn-screening-input"
                placeholder="Type your answer..."
                value={answers[i]}
                onChange={(e) => handleChange(i, e.target.value)}
                disabled={submitted || disabled}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && i === questions.length - 1) {
                    handleSubmit();
                  }
                }}
              />
            </div>
          );
        })}
      </div>

      <div className="hn-screening-extra" style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '16px', padding: '0 16px' }}>
        <div className="hn-screening-question" style={{ marginBottom: 0 }}>
          <label className="hn-screening-label">
            Status <span style={{ color: '#ff4d4f' }}>*</span>
          </label>
          <select
            className="hn-screening-input"
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            disabled={submitted || disabled}
            style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid rgba(255, 255, 255, 0.1)', background: 'rgba(0, 0, 0, 0.2)', color: '#fff' }}
          >
            <option value="">Select The Status</option>
            <option value="SHORTLIST">Move to shortlisting</option>
            <option value="INTERVIEW">Move to interview</option>
            <option value="REJECT">Reject</option>
          </select>
        </div>
        <div className="hn-screening-question" style={{ marginBottom: 0 }}>
          <label className="hn-screening-label">
            Comment {status !== "REJECT" && <span style={{ color: '#ff4d4f' }}>*</span>}
          </label>
          <textarea
            className="hn-screening-input"
            placeholder="Add a comment..."
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            disabled={submitted || disabled}
            rows="3"
            style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid rgba(255, 255, 255, 0.1)', background: 'rgba(0, 0, 0, 0.2)', color: '#fff', resize: 'vertical' }}
          />
        </div>
      </div>

      {/* Submit button */}
      <button
        className={`hn-screening-submit ${
          !allAnswered || submitted || disabled ? "hn-screening-submit-disabled" : ""
        }`}
        onClick={handleSubmit}
        disabled={!allAnswered || submitted || disabled}
      >
        {submitted ? (
          <>
            <span className="hn-step-spinner" />
            Evaluating...
          </>
        ) : (
          <>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M22 2L11 13" />
              <path d="M22 2L15 22L11 13L2 9L22 2Z" />
            </svg>
            Submit Answers
          </>
        )}
      </button>
    </div>
  );
}
